import { useState } from 'react';
import { toast } from 'sonner';
import { useAppStore } from '../stores/appStore';
import { userAuthApi } from '../lib/userAuthApi';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';
import { UserCircle, Lock, Mail } from 'lucide-react';

export default function Profile() {
  const currentUser = useAppStore((state) => state.currentUser);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.id) {
      toast.error('User not found. Please log in again.');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setIsLoading(true);

      await userAuthApi.changePassword(currentUser.id, {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('Password changed successfully');

      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error('Failed to change password. Please check your current password and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <UserCircle className="w-8 h-8 text-secondary-500" />
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Profile</h1>
          <p className="text-sm text-neutral-600">Manage your account settings</p>
        </div>
      </div>

      {/* User Information Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">User Information</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <UserCircle className="w-10 h-10 text-secondary-500" />
              <div>
                <p className="text-sm text-neutral-600">Name</p>
                <p className="font-medium text-neutral-900">{currentUser?.name || 'Guest User'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <Mail className="w-10 h-10 text-secondary-500" />
              <div>
                <p className="text-sm text-neutral-600">Email</p>
                <p className="font-medium text-neutral-900">{currentUser?.email || 'guest@example.com'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <Lock className="w-10 h-10 text-secondary-500" />
              <div>
                <p className="text-sm text-neutral-600">Role</p>
                <p className="font-medium text-neutral-900">{currentUser?.role || 'Consultant'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Change Password</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <Input
              label="New Password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Password must be at least 8 characters"
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                loading={isLoading}
                disabled={isLoading}
              >
                Change Password
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
